import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        // Ensure all items have a type field (migration for old cart items)
        const migratedCart = parsedCart.map(item => ({
          ...item,
          type: item.type || 'Physical' // Default to Physical if type is missing
        }));
        setCartItems(migratedCart);
      } catch (err) {
        console.error('Error loading cart from localStorage:', err);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    // Check if cart is empty - if so, allow any type
    if (cartItems.length === 0) {
      setCartItems([item]);
      return { success: true };
    }

    // Get the type of the new item
    const newItemType = item.type || 'Physical'; // Default to Physical if type not specified

    // Check if cart has items of a different type
    const existingItemType = cartItems[0].type || 'Physical';
    
    if (existingItemType !== newItemType) {
      const existingTypeLabel = existingItemType === 'Digital' ? 'Instant Digital Download' : 'Physical';
      const newTypeLabel = newItemType === 'Digital' ? 'Instant Digital Download' : 'Physical';
      return { 
        success: false, 
        error: `Cannot mix ${existingTypeLabel} and ${newTypeLabel} products in the same cart. Please complete your current order or remove items from your cart first.` 
      };
    }

    // Types match, proceed with adding/updating
    // Check if item already exists in cart
    // For format-based items: match by designId + formatId
    // For mockup-only items (no formatId): match by designId only
    const existingIndex = cartItems.findIndex(i => {
      // Bundle items match by bundleId
      if (item.itemType === 'bundle') {
        return i.itemType === 'bundle' && i.bundleId === item.bundleId;
      }
      // Never match a normal item against a bundle line
      if (i.itemType === 'bundle') {
        return false;
      }
      if (item.formatId === null || item.formatId === undefined) {
        // Mockup-only item: match by designId only (and no formatId)
        return i.designId === item.designId && (i.formatId === null || i.formatId === undefined);
      } else {
        // Format-based item: match by designId + formatId
        return i.designId === item.designId && i.formatId === item.formatId;
      }
    });

    // For digital products, prevent duplicates
    if (newItemType === 'Digital' && existingIndex >= 0) {
      return {
        success: false,
        error: 'This Instant Digital Download is already in your cart.'
      };
    }

    // For physical products, allow quantity updates
    setCartItems(prevItems => {
      if (existingIndex >= 0) {
        // Update quantity if item exists (physical products only)
        const updated = [...prevItems];
        updated[existingIndex].quantity += item.quantity;
        return updated;
      } else {
        // Add new item
        return [...prevItems, item];
      }
    });

    return { success: true };
  };

  const removeFromCart = (index) => {
    setCartItems(prevItems => prevItems.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }
    setCartItems(prevItems => {
      const updated = [...prevItems];
      updated[index].quantity = quantity;
      return updated;
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartItemCount,
        getCartTotal
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

